import { useCallback, useEffect, useState } from 'react'
import type { Patient } from '@medplum/fhirtypes'
import type { ThreadsFilterOptions } from './components/FilterControls'
import type { MessageThreadSummary, ThreadStatus } from './service'
import { clearSessionState, useSessionState } from '@/hooks/useSessionState'
import { logError } from '@/utils/errorUtils'
import { useMessagesUnread } from './useMessagesUnread'

const THREAD_KEY_PREFIX = 'messages:selectedThread:'

export type MessagesPageStateController = {
  preselectedPatient?: Patient | null
  threadListRefreshKey: number
  selectedThread?: MessageThreadSummary
  threadStatusMap: Record<string, ThreadStatus>
  threadsFilterOptions: ThreadsFilterOptions
  setSelectedThread: (value?: MessageThreadSummary) => void
  setThreadStatusMap: (value: Record<string, ThreadStatus>) => void
  setThreadsFilterOptions: (value: ThreadsFilterOptions) => void
  handleThreadCreated: (created?: MessageThreadSummary) => void
  handleThreadRead: (threadId: string) => void
  handleThreadUpdated: (thread: MessageThreadSummary) => void
}

/**
 * Forgets the thread a patient's chart panel last had open, before a link opens it at another
 * one: restoring the old thread would load it, and mark it read, while the list loads.
 */
export function forgetSelectedThread(patientId: string): void {
  clearSessionState(`${THREAD_KEY_PREFIX}${patientId}`)
}

export function useMessagesPageState(
  preselectedPatient?: Patient | null,
): MessagesPageStateController {
  const [threadListRefreshKey, setThreadListRefreshKey] = useState(0)
  // Kept for the session so returning to Messages reopens the thread the user
  // had open instead of the unselected state (PD-1206). Keyed by the
  // preselected patient so the patient-chart panel, which mounts this same
  // hook filtered to one patient, keeps its own selection.
  const threadKey = `messages:selectedThread:${preselectedPatient?.id ?? 'all'}`
  const [selectedThread, setSelectedThread] = useSessionState<MessageThreadSummary | undefined>(
    threadKey,
    undefined,
  )
  // Keep the unscoped /messages entry plus exactly one patient entry. Visiting the
  // unscoped tab must not evict the recent chart; selecting another patient does.
  // This bounds the keyspace without weakening cross-patient isolation (PD-1206).
  useEffect(() => {
    const allThreadKey = `${THREAD_KEY_PREFIX}all`
    if (threadKey !== allThreadKey) {
      clearSessionState(THREAD_KEY_PREFIX, [threadKey, allThreadKey])
    }
  }, [threadKey])
  const [threadStatusMap, setThreadStatusMap] = useState<Record<string, ThreadStatus>>({})
  const [threadsFilterOptions, setThreadsFilterOptions] = useState<ThreadsFilterOptions>({
    selectedPatient: preselectedPatient ?? null,
    showUrgentOnly: false,
    showFollowUpOnly: false,
  })
  const { setHasUnreadMessages, refreshUnreadState } = useMessagesUnread()

  // Feed the nav indicator from the loaded thread list — but only when this view
  // is showing *every* thread. The patient-chart panel filters by patient, and
  // publishing that subset used to switch the indicator off while unread threads
  // for other patients remained. This view reloads on every incoming message, so
  // it keeps the indicator live without waiting on a second request.
  const isUnfilteredThreadList = !threadsFilterOptions.selectedPatient
  useEffect(() => {
    if (!isUnfilteredThreadList) {
      return
    }

    const statuses = Object.values(threadStatusMap)
    if (statuses.length === 0) {
      return
    }

    setHasUnreadMessages(statuses.some((status) => status === 'unread'))
  }, [isUnfilteredThreadList, setHasUnreadMessages, threadStatusMap])

  useEffect(() => {
    setThreadsFilterOptions((prev) => {
      const nextPatientId = preselectedPatient?.id ?? null
      const currentPatientId = prev.selectedPatient?.id ?? null

      if (currentPatientId === nextPatientId) {
        return prev
      }

      return {
        ...prev,
        selectedPatient: preselectedPatient ?? null,
      }
    })
  }, [preselectedPatient])

  const handleThreadCreated = useCallback(
    (created?: MessageThreadSummary) => {
      // Open the new conversation right away instead of leaving the creator on
      // whichever thread they had selected. The endpoint returns the same summary
      // the list uses, so this does not have to wait for the reload below.
      if (created?.thread?.id) {
        setSelectedThread(created)
      }

      setThreadListRefreshKey((value) => value + 1)
    },
    [setSelectedThread],
  )

  const handleThreadRead = useCallback(
    (threadId: string) => {
      setThreadStatusMap((prev) => ({
        ...prev,
        [threadId]: 'read',
      }))

      // The nav indicator is owned by MessagesUnreadContext, which counts *all*
      // threads. Deriving it from this map instead would let a patient-filtered
      // view (the patient-chart panel) switch the indicator off while unread
      // threads for other patients remain.
      refreshUnreadState().catch((err) =>
        logError('messages', 'Failed to refresh unread state after marking thread read', err),
      )
    },
    [refreshUnreadState],
  )

  const handleThreadUpdated = useCallback(
    (thread: MessageThreadSummary) => {
      setSelectedThread(thread)
      setThreadListRefreshKey((value) => value + 1)
    },
    [setSelectedThread],
  )

  return {
    preselectedPatient,
    threadListRefreshKey,
    selectedThread,
    threadStatusMap,
    threadsFilterOptions,
    setSelectedThread,
    setThreadStatusMap,
    setThreadsFilterOptions,
    handleThreadCreated,
    handleThreadRead,
    handleThreadUpdated,
  }
}
